import type { TableData } from "@/components/editor/table-grid/tableModel";
import { cellColspan, cellText, placeRow, tableWidth } from "@/components/editor/table-grid/tableCells";

/**
 * Tile clipping: how much of a table a fixed box shows.
 *
 * The preview is for **recognition, not reading**, and it never scales type
 * (SPEC.md §14, carried into Olivia's tables-modern drawing):
 *
 *   1. Type stays at its size; the box decides how much of the table shows.
 *   2. Whole columns, left to right, until the box is full; then stop.
 *   3. A prose column may truncate; a numeric column never does: it is dropped
 *      whole, because a clipped number is wrong in a way a clipped word is not.
 *   4. The row at the clip line is shown as half a row. That half row is the
 *      "there is more" signal; nothing is drawn over the table.
 *   5. What did not fit is counted beside it: `+5 cols · +8 rows`, `+6 rows`,
 *      or nothing when everything fits.
 *   6. The header row and the first column always survive.
 *
 * Pure: widths are estimated from character counts, so the rules can be tested
 * without a browser. The estimate only decides which columns go in; the
 * renderer lays them out with real fonts.
 */

export interface TileMetrics {
  /** Box size, px. */
  width: number;
  height: number;
  /** Cell text size, px. */
  fontPx: number;
  /** Header row and body row heights, px. */
  headerH: number;
  rowH: number;
  /** Horizontal padding inside each cell, px (both sides together). */
  cellPadX: number;
  /** A prose column narrower than this is dropped rather than truncated. */
  minProsePx?: number;
}

export interface TileColumn {
  /** Index into the table's columns. */
  index: number;
  /** Estimated width, px; use as a weight when laying out. */
  width: number;
  numeric: boolean;
  /** Prose that did not fit whole: shown with an ellipsis. */
  truncated: boolean;
}

export interface TileLayout {
  columns: TileColumn[];
  /** Body rows shown whole. */
  fullRows: number;
  /** One more row, shown as half a row at the clip line. */
  halfRow: boolean;
  droppedCols: number;
  /** Body rows not shown whole (the half row counts as not shown). */
  droppedRows: number;
  totalCols: number;
  totalRows: number;
}

/** The table as text, one string per column, spans placed at their start. */
export interface TileText {
  header: string[] | null;
  rows: string[][];
  width: number;
}

/**
 * Average glyph width as a share of the font size, for serif text, on the
 * generous side: an estimate that runs short truncates numbers, which rule 3
 * forbids, while one that runs long only drops a column a little early.
 */
export const GLYPH = 0.6;
/** Columns never get wider than this many characters in a tile. */
const MAX_CHARS = 28;

const NUMERIC = /^[-+−–]?\s*[\d.,]+(?:[eE][-+−]?\d+)?\s*(?:%|[kKMBG]|×)?$/;

/** A cell that reads as a number: `76.1`, `−1.4`, `25.6M`, `50.7%`, `1,204`. */
export function isNumericText(s: string): boolean {
  const t = s.replace(/\$|\\,|\\!|\{|\}/g, "").replace(/\\pm.*$/, "").trim();
  return t.length > 0 && NUMERIC.test(t);
}

/** Every non-empty body cell in the column is a number (an em dash counts as empty). */
export function isNumericColumn(rows: string[][], c: number): boolean {
  const vals = rows.map((r) => (r[c] ?? "").trim()).filter((v) => v && v !== "—" && v !== "-");
  return vals.length > 0 && vals.every(isNumericText);
}

/** The table's cells as plain text on a regular grid. */
export function tileText(data: TableData): TileText {
  const width = tableWidth(data.header, data.rows, data.hasHeader);
  let occupied: number[] = [];
  const place = (row: TableData["rows"][number]): string[] => {
    const out = Array.from({ length: width }, () => "");
    const placed = placeRow(row, occupied);
    occupied = placed.occupied;
    row.forEach((cell, i) => {
      const at = placed.starts[i];
      if (at < width) out[at] = cellText(cell);
      // A spanned cell's text sits in its first column; the rest stay empty.
      for (let k = 1; k < cellColspan(cell) && at + k < width; k++) out[at + k] = "";
    });
    return out;
  };
  const header = data.hasHeader ? place(data.header) : null;
  const rows = data.rows.map(place);
  return { header, rows, width };
}

const textPx = (s: string, fontPx: number) => Math.min(s.length, MAX_CHARS) * fontPx * GLYPH;

export function layoutTile(text: TileText, m: TileMetrics): TileLayout {
  const minProse = m.minProsePx ?? 48;
  const totalCols = text.width;
  const totalRows = text.rows.length;

  const natural = (c: number) => {
    // The header is set bold, which runs about a tenth wider.
    const head = text.header ? textPx(text.header[c] ?? "", m.fontPx) * 1.1 : 0;
    const widest = text.rows.reduce((w, r) => Math.max(w, textPx(r[c] ?? "", m.fontPx)), Math.max(head, m.fontPx));
    return Math.ceil(widest + m.cellPadX - 1e-9);
  };

  const columns: TileColumn[] = [];
  let used = 0;
  for (let c = 0; c < totalCols; c++) {
    const w = natural(c);
    const numeric = isNumericColumn(text.rows, c);
    const room = m.width - used;
    if (w <= room) {
      columns.push({ index: c, width: w, numeric, truncated: false });
      used += w;
      continue;
    }
    // The first column always survives, truncated to the room there is.
    if (c === 0) {
      columns.push({ index: 0, width: Math.max(room, 1), numeric, truncated: true });
      used = m.width;
      break;
    }
    if (!numeric && room >= minProse) {
      columns.push({ index: c, width: room, numeric, truncated: true });
      used = m.width;
    }
    break;
  }

  // Rows: header first, then whole rows, then half a row when more remain.
  const bodyRoom = Math.max(0, m.height - (text.header ? m.headerH : 0));
  let fullRows: number;
  let halfRow = false;
  if (totalRows * m.rowH <= bodyRoom) {
    fullRows = totalRows;
  } else {
    fullRows = Math.max(0, Math.floor((bodyRoom - m.rowH / 2) / m.rowH));
    fullRows = Math.min(fullRows, totalRows);
    halfRow = fullRows < totalRows;
  }

  return {
    columns,
    fullRows,
    halfRow,
    droppedCols: totalCols - columns.length,
    droppedRows: totalRows - fullRows,
    totalCols,
    totalRows,
  };
}

/** `+5 cols · +8 rows`, `+6 rows`, or "" when everything fits (rule 5). */
export function tileRemainder(t: Pick<TileLayout, "droppedCols" | "droppedRows">): string {
  const parts: string[] = [];
  if (t.droppedCols > 0) parts.push(`+${t.droppedCols} col${t.droppedCols === 1 ? "" : "s"}`);
  if (t.droppedRows > 0) parts.push(`+${t.droppedRows} row${t.droppedRows === 1 ? "" : "s"}`);
  return parts.join(" · ");
}
